import React, {FC, useCallback} from "react";
import {Code} from "@chakra-ui/react";
import {MailTemplateEditor, MailTemplatePreviewContext} from "./MailTemplateEditor";
import {
    DEFAULT_TEXT_AFTER_PRODUCTS,
    DEFAULT_TEXT_BEFORE_PRODUCTS,
    buildComposedEmailHtmlFromFormFields,
    defaultInactiveOrderReminderPreviewSample,
    previewInactiveOrderReminderHtml,
    previewInactiveOrderReminderHtmlFromParts,
    previewInactiveOrderReminderSubjectFromPlain,
} from "../../shared/utils/inactiveOrderReminderEmailPreview";
import {DEALERSHIP_NAME_TAG, PLAIN_SUBJECT_PREFIX} from "../../shared/utils/inactiveOrderReminderMailPlainText";

type Props = {
    subject: string;
    textBefore: string;
    textAfter: string;
    previewDealershipName: string;
    emailHtml: string;
    useCustomHtml: boolean;
    onSubjectChange: (value: string) => void;
    onTextBeforeChange: (value: string) => void;
    onTextAfterChange: (value: string) => void;
    onPreviewDealershipNameChange: (value: string) => void;
    onEmailHtmlChange: (value: string) => void;
    onUseCustomHtmlChange: (value: boolean) => void;
};

/** Order reminder email editor — uses shared {@link MailTemplateEditor}. */
export const InactiveOrderReminderEmailEditor: FC<Props> = (props) => {
    const buildPreview = useCallback((ctx: MailTemplatePreviewContext) => {
        const sample = {
            ...defaultInactiveOrderReminderPreviewSample(),
            dealershipName: ctx.previewDealershipName.trim() || "Sample Dealership LLC",
        };
        const previewSubject = previewInactiveOrderReminderSubjectFromPlain(ctx.subject, sample);
        const useHtmlPreview = ctx.htmlEditorOpen && ctx.useCustomHtml && ctx.emailHtml.trim();
        const previewHtml = useHtmlPreview
            ? previewInactiveOrderReminderHtml(ctx.emailHtml, sample)
            : previewInactiveOrderReminderHtmlFromParts(ctx.textBefore, ctx.textAfter, sample);
        return {previewSubject, previewHtml};
    }, []);

    return (
        <MailTemplateEditor
            {...props}
            buildPreview={buildPreview}
            buildComposedHtml={buildComposedEmailHtmlFromFormFields}
            subjectPlaceholder={PLAIN_SUBJECT_PREFIX}
            textBeforePlaceholder={DEFAULT_TEXT_BEFORE_PRODUCTS}
            textAfterPlaceholder={DEFAULT_TEXT_AFTER_PRODUCTS}
            dealershipTag={DEALERSHIP_NAME_TAG}
            htmlEditorFooter={
                <>
                    Full HTML (FreeMarker). Product list block:{" "}
                    <Code fontSize="xs">{"<#list products as product>..."}</Code>
                </>
            }
        />
    );
};

export default InactiveOrderReminderEmailEditor;
